/* MediaPicker — admin picker that handles BOTH images and videos in one
 * browser, plus an external-URL input so you can paste any public URL.
 *
 * Usage:
 *   <MediaPicker
 *     label="Hero media"
 *     value={state.url}
 *     onChange={(url, meta) => setState({ url, kind: meta.kind })}
 *   />
 *
 * `meta.kind` is "image" | "video" | "unknown" — callers can use it
 * to render the correct element on the public site. For stored Media
 * Library items we use their `content_type`. For pasted external
 * URLs we infer from the extension (.mp4/.webm/.mov → video; png/jpg
 * /webp/etc → image; fallback "unknown" which defaults to image).
 *
 * Layout rule everywhere on the public site: every media slot uses
 *   object-fit: cover; object-position: center center;
 * so ANY resolution fills the slot centered on the middle of the
 * file — no letterboxing, no edge-cropping surprises.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import { Btn } from "./atoms";

const fullUrl = (url) =>
  !url
    ? ""
    : /^https?:\/\//i.test(url) || url.startsWith("data:")
    ? url
    : `${api.base}${url}`;

/** Return "image" | "video" | "unknown" for a given URL. */
export const kindFromUrl = (url) => {
  if (!url) return "unknown";
  const clean = String(url).toLowerCase().split("?")[0];
  if (/\.(mp4|webm|mov|m4v|ogv)$/.test(clean)) return "video";
  if (/\.(png|jpe?g|webp|gif|svg|avif|heic|heif)$/.test(clean)) return "image";
  return "unknown";
};

/** Render a thumbnail for any media item, centered-crop.
 *  Videos render as a small muted autoplaying loop seeked ~25% in so
 *  the thumb shows meaningful content (avoids dark intro frames). If
 *  the video element fires onError (codec mismatch, network issue, or
 *  CORS) we swap to a graceful "VIDEO PREVIEW" placeholder with a play
 *  glyph instead of an empty black box. */
function Thumb({ url, kind, className = "" }) {
  const ref = useRef(null);
  const [errored, setErrored] = useState(false);
  const src = fullUrl(url);
  if (!src) return null;
  if (kind === "video") {
    if (errored) {
      return (
        <div
          className={`w-full h-full flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-[#1A2230] to-[#0B0F14] ${className}`}
          data-testid="mp-thumb-fallback"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-[#F4D64E]" fill="currentColor" aria-hidden>
            <path d="M8 5v14l11-7z" />
          </svg>
          <span className="text-[0.55rem] font-bold tracking-[0.22em] uppercase text-neutral-400">
            Preview
          </span>
        </div>
      );
    }
    return (
      <video
        ref={ref}
        src={src}
        muted
        playsInline
        preload="metadata"
        onLoadedMetadata={() => {
          const v = ref.current;
          if (v && v.duration && Number.isFinite(v.duration)) {
            // Seek a quarter of the way in to skip black title cards.
            try { v.currentTime = Math.min(v.duration * 0.25, 3); } catch (_) {}
          }
        }}
        onError={() => setErrored(true)}
        className={`w-full h-full object-cover object-center ${className}`}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      className={`w-full h-full object-cover object-center ${className}`}
    />
  );
}

function TypeBadge({ kind }) {
  const label = kind === "video" ? "VIDEO" : "IMG";
  const color =
    kind === "video"
      ? "text-[#F4D64E] border-[#F4D64E]/40 bg-[#F4D64E]/10"
      : "text-neutral-300 border-white/[0.15] bg-white/[0.05]";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[0.55rem] font-bold tracking-[0.25em] border ${color}`}
    >
      {label}
    </span>
  );
}

export function MediaPicker({ value, onChange, label = "Media", hint }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | image | video
  const [externalUrl, setExternalUrl] = useState("");

  useEffect(() => {
    if (open && items === null) api.mediaList().then(setItems);
  }, [open, items]);

  const currentKind = kindFromUrl(value);

  const filtered = (items || []).filter((m) => {
    const itemKind = (m.content_type || "").startsWith("video/")
      ? "video"
      : "image";
    if (filter !== "all" && itemKind !== filter) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (
      (m.filename || "").toLowerCase().includes(t) ||
      (m.alt || "").toLowerCase().includes(t)
    );
  });

  const pick = (url, kind) => {
    onChange(url, { kind });
    setOpen(false);
    setExternalUrl("");
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const m = await api.mediaUpload(f);
      setItems(items ? [m, ...items] : [m]);
      const kind = (m.content_type || "").startsWith("video/") ? "video" : "image";
      pick(m.url, kind);
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onUseExternal = () => {
    const u = externalUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u) && !u.startsWith("/")) {
      alert("Please paste a full URL starting with https:// (or a relative /uploads/... path).");
      return;
    }
    pick(u, kindFromUrl(u));
  };

  return (
    <div data-testid="media-picker">
      <span className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
        {label}
      </span>
      {hint ? (
        <p className="text-[0.68rem] text-neutral-500 mb-3 leading-relaxed">{hint}</p>
      ) : null}
      <div className="flex items-start gap-3">
        <div className="relative w-36 h-24 rounded-md border border-white/[0.10] bg-[#0B0F14] overflow-hidden shrink-0">
          {value ? (
            <>
              <Thumb url={value} kind={currentKind} />
              <div className="absolute top-1.5 left-1.5">
                <TypeBadge kind={currentKind === "video" ? "video" : "image"} />
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[0.6rem] tracking-[0.2em] uppercase text-neutral-600">
              Empty
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <Btn variant="ghost" onClick={() => setOpen(true)} data-testid="mp-open">
            {value ? "Change…" : "Choose media"}
          </Btn>
          {value ? (
            <Btn
              variant="ghost"
              onClick={() => onChange("", { kind: "unknown" })}
              data-testid="mp-clear"
              className="!text-[0.6rem]"
            >
              Remove
            </Btn>
          ) : null}
          {value ? (
            <div className="text-[0.6rem] tracking-[0.15em] uppercase text-neutral-600 break-all max-w-xs" data-testid="mp-current-url">
              {value.length > 60 ? `${value.slice(0, 57)}…` : value}
            </div>
          ) : null}
        </div>
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center px-6 py-12 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          data-testid="mp-modal"
        >
          <div className="bg-[#0F141A] border border-white/[0.10] rounded-xl p-7 max-w-5xl w-full">
            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div>
                <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
                  Media library
                </div>
                <h3 className="mt-2 font-display text-xl font-black tracking-[-0.02em] text-white">
                  Choose image or video
                </h3>
                <p className="mt-1 text-xs text-neutral-500 max-w-md">
                  Every slot accepts any image or video. Media is cropped to
                  fit the slot, centered on the middle of the file.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="search"
                  placeholder="Search filename…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 w-56"
                  data-testid="mp-search"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={onUpload}
                    className="hidden"
                    data-testid="mp-upload"
                  />
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/40 hover:bg-[#F4D64E]/10 transition-colors">
                    {busy ? "Uploading…" : "＋ Upload new"}
                  </span>
                </label>
                <Btn variant="ghost" onClick={() => setOpen(false)} data-testid="mp-close">
                  Close
                </Btn>
              </div>
            </div>

            {/* External URL input */}
            <div
              data-testid="mp-external"
              className="mb-5 p-4 rounded-lg border border-white/[0.08] bg-[#0B0F14]"
            >
              <div className="text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
                Or paste an external URL
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="url"
                  placeholder="https://images.unsplash.com/... or https://cdn.yoursite.com/video.mp4"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  className="flex-1 min-w-[260px] bg-[#0F141A] border border-white/[0.10] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60"
                  data-testid="mp-external-url"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onUseExternal();
                  }}
                />
                <Btn onClick={onUseExternal} disabled={!externalUrl.trim()} data-testid="mp-external-use">
                  Use this URL
                </Btn>
              </div>
            </div>

            {/* Type filter pills */}
            <div className="flex items-center gap-2 mb-5">
              {[
                { v: "all", label: "All" },
                { v: "image", label: "Images" },
                { v: "video", label: "Videos" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFilter(opt.v)}
                  data-testid={`mp-filter-${opt.v}`}
                  className={`px-3 py-1.5 rounded-md text-[0.62rem] font-bold tracking-[0.22em] uppercase border transition-colors ${
                    filter === opt.v
                      ? "border-[#F4D64E]/60 text-[#F4D64E] bg-[#F4D64E]/10"
                      : "border-white/[0.10] text-neutral-400 hover:text-white hover:border-white/[0.25]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {items === null ? (
              <div className="text-sm text-neutral-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-neutral-500">
                No matching media. Upload one or paste an external URL above.
              </div>
            ) : (
              <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[55vh] overflow-y-auto pr-1">
                {filtered.map((m) => {
                  const itemKind = (m.content_type || "").startsWith("video/")
                    ? "video"
                    : "image";
                  const active = m.url === value;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        data-testid={`mp-item-${m.id}`}
                        onClick={() => pick(m.url, itemKind)}
                        className={`w-full text-left rounded-md overflow-hidden border transition-colors ${
                          active
                            ? "border-[#F4D64E]"
                            : "border-white/[0.06] hover:border-white/[0.20]"
                        }`}
                      >
                        <div className="relative aspect-[4/3] bg-black/40">
                          <Thumb url={m.url} kind={itemKind} className="absolute inset-0" />
                          <div className="absolute top-2 left-2">
                            <TypeBadge kind={itemKind} />
                          </div>
                        </div>
                        <div className="p-2 text-[0.65rem] truncate text-neutral-400 font-mono">
                          {m.filename}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
